import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { landingAPI, enrollmentAPI, studentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2,
  CheckCircle,
  Calendar,
  MapPin,
  Clock,
  Users,
  ArrowLeft,
  MessageCircle,
  AlertCircle,
  Waves,
} from 'lucide-react';
import { toast } from 'sonner';

const ProgramEnrollment = () => {
  const { programId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState(null);
  
  const [config, setConfig] = useState(null);
  const [program, setProgram] = useState(null);
  const [existingStudents, setExistingStudents] = useState([]);
  
  const [form, setForm] = useState({
    program_id: programId || '',
    location_id: searchParams.get('location') || '',
    student_name: '',
    student_dob: '',
    preferred_schedule: '',
    guardian_name: user?.name || '',
    guardian_phone: '',
    guardian_email: user?.email || '',
    notes: '',
    existing_student_id: '',
    use_existing: false,
  });

  useEffect(() => {
    loadData();
  }, [programId]);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        guardian_name: prev.guardian_name || user.name,
        guardian_email: prev.guardian_email || user.email,
      }));
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load landing config
      const configRes = await landingAPI.getConfig();
      setConfig(configRes.data);
      
      // Find the specific program
      const foundProgram = configRes.data.programs?.find(p => p.id === programId);
      setProgram(foundProgram);
      
      // Load existing students for parent users
      if (user?.role === 'parent' && user?.student_ids?.length > 0) {
        try {
          const studentsRes = await studentsAPI.getAll();
          const myStudents = studentsRes.data.filter(s => 
            user.student_ids.includes(s.id)
          );
          setExistingStudents(myStudents);
        } catch (e) {
          // Ignore - user may not have students yet
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.location_id) {
      toast.error('Please select a location');
      return;
    }
    
    if (!form.use_existing && !form.student_name) {
      toast.error('Please enter student name');
      return;
    }
    
    if (!form.guardian_name || !form.guardian_phone) {
      toast.error('Please enter guardian name and phone number');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const enrollmentData = {
        program_id: form.program_id,
        location_id: form.location_id,
        student_name: form.use_existing 
          ? existingStudents.find(s => s.id === form.existing_student_id)?.name || form.student_name
          : form.student_name,
        student_dob: form.student_dob || null,
        preferred_schedule: form.preferred_schedule || null,
        guardian_name: form.guardian_name,
        guardian_phone: form.guardian_phone,
        guardian_email: form.guardian_email || null,
        notes: form.notes || null,
        existing_student_id: form.use_existing ? form.existing_student_id : null,
      };
      
      const res = await enrollmentAPI.create(enrollmentData);
      setEnrollmentId(res.data.enrollment_id);
      setSubmitted(true);
      toast.success('Enrollment request submitted successfully!');
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = `Hello Eswatini Seals, I'd like to enroll for ${program?.name || 'swimming lessons'} at ${form.location_id || 'your academy'}. Please assist.`;
    window.open(`https://wa.me/26876817515?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Success State
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Enrollment Submitted!
            </h1>
            
            <p className="text-slate-600 mb-6">
              Your enrollment request for <strong>{program?.name}</strong> has been submitted successfully 
              and is pending admin approval.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1">
                <AlertCircle className="w-4 h-4" />
                Status: Pending Approval
              </div>
              <p className="text-xs text-amber-600">
                You will be notified once your enrollment is approved. Check your dashboard for updates.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate('/dashboard')} className="flex-1">
                Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
                Back to Home
              </Button>
            </div>
            
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-slate-500 mb-3">Need immediate assistance?</p>
              <Button variant="outline" onClick={openWhatsApp} className="text-green-600 border-green-300 hover:bg-green-50">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp: 7681 7515
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2 text-slate-600 hover:text-primary"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Waves className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-primary">Eswatini Seals</span>
          </div>
          
          <div className="text-sm text-slate-500">
            {user?.name}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Program Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Program Details</CardTitle>
              </CardHeader>
              <CardContent>
                {program ? (
                  <div className="space-y-4">
                    {program.image_url && (
                      <img 
                        src={program.image_url} 
                        alt={program.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    )}
                    
                    <h3 className="font-heading text-xl font-bold text-slate-900">
                      {program.name}
                    </h3>
                    
                    <p className="text-sm text-slate-600">{program.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>{program.duration} • {program.schedule}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Users className="w-4 h-4" />
                        <span>{program.ages}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span>{program.locations?.join(', ')}</span>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <span className="text-2xl font-bold text-primary">{program.fee}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {program.focus?.map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500">Program not found</p>
                )}
                
                {/* WhatsApp Fallback */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-2">Need help?</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={openWhatsApp}
                    className="w-full text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp Support
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enrollment Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Enrollment Form</CardTitle>
                <p className="text-sm text-slate-500">
                  Complete the form below to enroll. Your request will be reviewed by our team.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Location Selection */}
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Select 
                      value={form.location_id} 
                      onValueChange={(v) => setForm({ ...form, location_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.locations
                          ?.filter(loc => !program || program.locations?.includes(loc.name.split(' ')[0]))
                          .map((loc) => (
                            <SelectItem key={loc.id} value={loc.id}>
                              {loc.name} - {loc.address}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Student Selection / Info */}
                  <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900">Student Information</h4>
                    
                    {existingStudents.length > 0 && (
                      <div className="flex items-center gap-4 pb-4 border-b">
                        <label className="flex items-center gap-2">
                          <input 
                            type="radio" 
                            checked={!form.use_existing}
                            onChange={() => setForm({ ...form, use_existing: false, existing_student_id: '' })}
                          />
                          <span className="text-sm">New Student</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input 
                            type="radio" 
                            checked={form.use_existing}
                            onChange={() => setForm({ ...form, use_existing: true })}
                          />
                          <span className="text-sm">Existing Student</span>
                        </label>
                      </div>
                    )}
                    
                    {form.use_existing ? (
                      <div className="space-y-2">
                        <Label>Select Student</Label>
                        <Select 
                          value={form.existing_student_id} 
                          onValueChange={(v) => setForm({ ...form, existing_student_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select existing student" />
                          </SelectTrigger>
                          <SelectContent>
                            {existingStudents.map((student) => (
                              <SelectItem key={student.id} value={student.id}>
                                {student.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Student Name *</Label>
                          <Input 
                            value={form.student_name}
                            onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                            placeholder="Full name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date of Birth</Label>
                          <Input 
                            type="date"
                            value={form.student_dob}
                            onChange={(e) => setForm({ ...form, student_dob: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Schedule Preference */}
                  <div className="space-y-2">
                    <Label>Preferred Schedule</Label>
                    <Input 
                      value={form.preferred_schedule}
                      onChange={(e) => setForm({ ...form, preferred_schedule: e.target.value })}
                      placeholder="e.g., Saturday mornings, Weekday evenings"
                    />
                  </div>

                  {/* Guardian Information */}
                  <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-slate-900">Parent/Guardian Information</h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input 
                          value={form.guardian_name}
                          onChange={(e) => setForm({ ...form, guardian_name: e.target.value })}
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone Number *</Label>
                        <Input 
                          value={form.guardian_phone}
                          onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })}
                          placeholder="+268 7XXX XXXX"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={form.guardian_email}
                        onChange={(e) => setForm({ ...form, guardian_email: e.target.value })}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea 
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Any special requirements, medical conditions, or questions?"
                      rows={3}
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Enrollment Request'
                      )}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/')}
                    >
                      Cancel
                    </Button>
                  </div>

                  <p className="text-xs text-slate-500 text-center">
                    Your enrollment will be pending until approved by our team. 
                    You&apos;ll receive a notification once it&apos;s processed.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramEnrollment;
